import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';

const ChevronIcon = ({ isHovered, isDarkMode }) => (
  <svg 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
    className="transition-colors duration-300"
  >
    <path 
      d="M6 9L12 15L18 9" 
      stroke={isHovered ? "#FCD535" : isDarkMode ? "#FFFFFF" : "#000000"}
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

const FAQSection = ({ faqs }) => {
  const { isDarkMode } = useTheme();
  const [activeIndex, setActiveIndex] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const listStyle = {
    listStyleType: 'disc',
    paddingLeft: '1.5rem',
    marginTop: '0.5rem',
  };
  
  return (
    <section className={`relative py-24 ${isDarkMode ? 'bg-[#000000]' : 'bg-white'}`}>
      {/* Background Color */}
      <div className={`absolute inset-0 ${isDarkMode ? 'bg-[#000000]' : 'bg-white'}`} />

      {/* Content */}
      <div className="relative z-10 max-w-[1300px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-24">
          {/* Left Column - Heading (4 columns = ~33%) */}
          <div className="lg:col-span-5">
            <motion.h2 
              className={`text-[30px] font-medium font-lexend sticky top-24 ${
                isDarkMode ? 'text-white' : 'text-black'
              }`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Frequently asked questions
            </motion.h2>
          </div>

          {/* Right Column - Questions & Answers (8 columns = ~66%) */}
          <div className="lg:col-span-7 mt-8 lg:mt-0">
            <div className="flex flex-col space-y-6">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  className="cursor-pointer relative pb-6"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => setActiveIndex(activeIndex === index ? null : index)}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  <div className="flex justify-between items-start group">
                    <h3 className={`text-[22px] font-regular pr-8 font-lexend leading-[auto] tracking-[0%] ${
                      isDarkMode ? 'text-white' : 'text-black'
                    } group-hover:text-[#FCD535] transition-colors duration-300`}>
                      {faq.question}
                    </h3>
                    <motion.div
                      animate={{ rotate: activeIndex === index ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <ChevronIcon isHovered={hoveredIndex === index} isDarkMode={isDarkMode} />
                    </motion.div>
                  </div>

                  <AnimatePresence>
                    {activeIndex === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className={`text-[18px] font-light mt-4 ml-4 font-lexend leading-[28px] tracking-[0%] ${
                          isDarkMode ? 'text-white/80' : 'text-black/70'
                        }`}>
                          {typeof faq.answer === 'string' ? (
                            <p>{faq.answer}</p>
                          ) : (
                            <div style={listStyle}>{faq.answer}</div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Gradient Border Line */}
                  <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#FCD535] to-transparent" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection; 